import React, { useState } from "react";
import { ChevronDown, ChevronUp, ShieldCheck, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const WhyTrustPanel = ({
  title = "Why trust this score?",
  formulaExplanation,
  verifiedSource,
  uncoveredNote,
  testId = "why-trust",
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-xs uppercase tracking-[0.18em] text-[var(--rg-sage)] hover:text-[var(--rg-text-strong)] font-semibold inline-flex items-center gap-1 transition-fast"
        data-testid={`${testId}-toggle`}
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        {title}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="mt-3 p-4 rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)]/80">
              {formulaExplanation && (
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)] mb-1">How this score was calculated</div>
                  <p className="text-sm text-[var(--rg-text)] leading-relaxed">{formulaExplanation}</p>
                </div>
              )}
              {uncoveredNote && (
                <div className="mb-4 rounded-md border border-[var(--rg-amber)]/40 bg-[var(--rg-amber)]/8 p-3" data-testid={`${testId}-uncovered`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-amber)] mb-1">Selected states not yet covered</div>
                  <p className="text-sm text-[var(--rg-text)] leading-relaxed">{uncoveredNote}</p>
                </div>
              )}
              {verifiedSource && (
                <div className="space-y-1.5 text-sm">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)]">Verified Source Proof</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
                    <div><span className="text-[var(--rg-muted)]">Authority:</span> <span className="text-[var(--rg-text-strong)]">{verifiedSource.authority}</span></div>
                    <div><span className="text-[var(--rg-muted)]">Citation:</span> <span className="text-[var(--rg-text-strong)] font-mono">{verifiedSource.citation}</span></div>
                    <div className="md:col-span-2">
                      <span className="text-[var(--rg-muted)]">Official URL:</span>{" "}
                      <a href={verifiedSource.url} target="_blank" rel="noreferrer" className="text-[var(--rg-sage)] hover:underline inline-flex items-center gap-1">
                        {verifiedSource.url} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div><span className="text-[var(--rg-muted)]">Last Verified:</span> <span className="text-[var(--rg-text-strong)] font-mono">{verifiedSource.lastVerified}</span></div>
                    <div><span className="text-[var(--rg-muted)]">Next Review:</span> <span className="text-[var(--rg-text-strong)] font-mono">{verifiedSource.nextReview}</span></div>
                  </div>
                  {verifiedSource.explanation && (
                    <div className="mt-3 pt-3 border-t border-[var(--rg-border)]">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)] mb-1">Why this applies to your store</div>
                      <p className="text-sm text-[var(--rg-text)]">{verifiedSource.explanation}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 text-xs">
                <a href="/methodology" className="text-[var(--rg-sage)] hover:underline">View full Methodology →</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WhyTrustPanel;
